import { apiRequest, API_BASE_URL, readResponsePayload } from "./apiClient";

function extractUploadsPath(imageUrl) {
  const safeUrl = String(imageUrl || "").trim();
  if (!safeUrl) {
    return "";
  }

  if (safeUrl.startsWith("/uploads/")) {
    return safeUrl;
  }

  try {
    const parsed = new URL(safeUrl);
    return parsed.pathname.startsWith("/uploads/") ? parsed.pathname : "";
  } catch (_error) {
    return "";
  }
}

export function resolveEventImageUrl(imageUrl) {
  const safeUrl = String(imageUrl || "").trim();
  if (!safeUrl) {
    return "";
  }

  const uploadsPath = extractUploadsPath(safeUrl);
  if (uploadsPath) {
    return `${API_BASE_URL}${uploadsPath}`;
  }

  return safeUrl;
}

export function resolveDishGalleryImageUrl(imageUrl) {
  return resolveEventImageUrl(imageUrl);
}

export function canCropEventImage(imageUrl) {
  const uploadsPath = extractUploadsPath(imageUrl);
  return uploadsPath.startsWith("/uploads/events/");
}

export async function fetchMenusFromApi() {
  const data = await apiRequest("/content/menus");
  return Array.isArray(data?.menus) ? data.menus : [];
}

export async function saveMenusToApi(menus) {
  const data = await apiRequest("/admin/menus", {
    method: "PUT",
    body: { menus },
  });

  return Array.isArray(data?.menus) ? data.menus : [];
}

export async function fetchImagesConfigFromApi() {
  const data = await apiRequest("/content/images");
  return data?.imagesConfig && typeof data.imagesConfig === "object"
    ? data.imagesConfig
    : {};
}

export async function saveImagesConfigToApi(imagesConfig) {
  const data = await apiRequest("/admin/images", {
    method: "PUT",
    body: { imagesConfig },
  });

  return data?.imagesConfig && typeof data.imagesConfig === "object"
    ? data.imagesConfig
    : {};
}

export async function fetchGoogleReviewsFromApi() {
  const data = await apiRequest("/content/google-reviews");
  const value = data?.googleReviews;

  return value && typeof value === "object" ? value : {};
}

export async function fetchGoogleReviewsStatusFromAdmin() {
  const data = await apiRequest("/admin/google-reviews/status");
  const value = data?.googleReviews;
  return value && typeof value === "object" ? value : {};
}

export async function refreshGoogleReviewsFromAdmin() {
  const data = await apiRequest("/admin/google-reviews/refresh", {
    method: "POST",
  });
  const value = data?.googleReviews;
  return value && typeof value === "object" ? value : {};
}

export async function fetchEventsFromApi() {
  const data = await apiRequest("/content/events");
  return Array.isArray(data?.events) ? data.events : [];
}

export async function fetchDishGalleryFromApi() {
  const data = await apiRequest("/content/dish-gallery");
  return Array.isArray(data?.dishGallery) ? data.dishGallery : [];
}

export async function fetchDishGalleryForAdmin() {
  const data = await apiRequest("/admin/dish-gallery");
  return Array.isArray(data?.dishGallery) ? data.dishGallery : [];
}

export async function saveDishGalleryToApi(dishGallery) {
  const data = await apiRequest("/admin/dish-gallery", {
    method: "PUT",
    body: { dishGallery },
  });

  return Array.isArray(data?.dishGallery) ? data.dishGallery : [];
}

export async function fetchEventsForAdmin() {
  const data = await apiRequest("/admin/events");
  return Array.isArray(data?.events) ? data.events : [];
}

export async function saveEventsToApi(events) {
  const data = await apiRequest("/admin/events", {
    method: "PUT",
    body: { events },
  });

  return Array.isArray(data?.events) ? data.events : [];
}

export async function uploadCroppedEventImage(blob) {
  const formData = new FormData();
  formData.append("image", blob, "event-crop.jpg");

  const response = await fetch(`${API_BASE_URL}/admin/upload-event-image`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  const payload = await readResponsePayload(response);
  const payloadObject = payload && typeof payload === "object" ? payload : null;
  if (!response.ok || !payloadObject?.success) {
    throw new Error(
      payloadObject?.error ||
        (typeof payload === "string" && payload.trim().slice(0, 140)) ||
        "Échec de l'upload.",
    );
  }

  // Store relative path so images remain portable across hosts/devices.
  return String(payload.imageUrl || "").trim();
}

export async function uploadDishGalleryImage(blob, title = "") {
  const formData = new FormData();
  formData.append("image", blob, "dish-photo.jpg");
  formData.append("title", String(title || "").trim());

  const response = await fetch(`${API_BASE_URL}/admin/upload-dish-image`, {
    method: "POST",
    credentials: "include",
    body: formData,
  });

  const payload = await readResponsePayload(response);
  const payloadObject = payload && typeof payload === "object" ? payload : null;
  if (!response.ok || !payloadObject?.success) {
    throw new Error(
      payloadObject?.error ||
        (typeof payload === "string" && payload.trim().slice(0, 140)) ||
        "Échec de l'upload.",
    );
  }

  return {
    imageUrl: String(payload.imageUrl || "").trim(),
    dishGallery: Array.isArray(payload.dishGallery) ? payload.dishGallery : [],
  };
}

export async function deleteEventImage(imageUrl) {
  try {
    const urlPath = extractUploadsPath(imageUrl);
    if (!urlPath.startsWith("/uploads/events/")) return;

    await fetch(`${API_BASE_URL}/admin/delete-event-image`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ imageUrl: urlPath }),
    });
  } catch (_error) {
    // Best-effort cleanup, don't block on failure
  }
}

export async function deleteDishGalleryImage(imageUrl) {
  try {
    const urlPath = extractUploadsPath(imageUrl);
    if (!urlPath.startsWith("/uploads/dish-gallery/")) return;

    const response = await fetch(`${API_BASE_URL}/admin/delete-dish-image`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ imageUrl: urlPath }),
    });

    const payload = await readResponsePayload(response);
    const payloadObject = payload && typeof payload === "object" ? payload : null;
    if (!response.ok || !payloadObject?.success) {
      throw new Error(
        payloadObject?.error ||
          (typeof payload === "string" && payload.trim().slice(0, 140)) ||
          "Échec de la suppression.",
      );
    }

    return {
      dishGallery: Array.isArray(payload.dishGallery) ? payload.dishGallery : [],
    };
  } catch (_error) {
    throw _error;
  }
}

export async function fetchOpeningHoursFromApi() {
  const data = await apiRequest("/content/opening-hours");
  return data?.openingHours || null;
}

export async function fetchOpeningHoursForAdmin() {
  const data = await apiRequest("/admin/opening-hours");
  return data?.openingHours || null;
}

export async function saveOpeningHoursToApi(openingHours) {
  const data = await apiRequest("/admin/opening-hours", {
    method: "PUT",
    body: openingHours,
  });
  return data?.openingHours || null;
}

export async function fetchTourismFromApi() {
  const data = await apiRequest("/content/tourism");
  return Array.isArray(data?.tourism) ? data.tourism : [];
}

export async function fetchTourismForAdmin() {
  const data = await apiRequest("/admin/tourism");
  return Array.isArray(data?.tourism) ? data.tourism : [];
}

export async function saveTourismToApi(tourism) {
  const data = await apiRequest("/admin/tourism", {
    method: "PUT",
    body: { tourism },
  });

  return Array.isArray(data?.tourism) ? data.tourism : [];
}

export async function fetchAllergensFromApi() {
  const data = await apiRequest("/content/allergens");
  return {
    dishes: Array.isArray(data?.dishes) ? data.dishes : [],
    labels: Array.isArray(data?.labels) ? data.labels : [],
  };
}

export async function fetchAllergensForAdmin() {
  const data = await apiRequest("/admin/allergens");
  return {
    dishes: Array.isArray(data?.dishes) ? data.dishes : [],
    labels: Array.isArray(data?.labels) ? data.labels : [],
  };
}

export async function saveAllergensToApi(dishes) {
  const data = await apiRequest("/admin/allergens", {
    method: "PUT",
    body: { dishes },
  });

  return {
    dishes: Array.isArray(data?.dishes) ? data.dishes : [],
    labels: Array.isArray(data?.labels) ? data.labels : [],
  };
}

export async function fetchSuppliersFromApi() {
  const data = await apiRequest("/content/suppliers");
  return Array.isArray(data?.suppliers) ? data.suppliers : [];
}

export async function fetchSuppliersForAdmin() {
  const data = await apiRequest("/admin/suppliers");
  return Array.isArray(data?.suppliers) ? data.suppliers : [];
}

export async function saveSuppliersToApi(suppliers) {
  const data = await apiRequest("/admin/suppliers", {
    method: "PUT",
    body: { suppliers },
  });

  return Array.isArray(data?.suppliers) ? data.suppliers : [];
}
